import { BroadcastChannel } from 'broadcast-channel';
import { useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

const defaultMessageConfig = {
  delay: 500,
  leading: true,
  maxWait: 1000,
};

type Timeout = ReturnType<typeof setTimeout>;

interface Config {
  /** Idle wait time before calling onIdle. Defaults to null, which disables the timers. Same as passing pause: true */
  idleTimeoutInMs: number | null;
  /** Idle wait time before calling onPrompt, will happen 30s before idleTimeInMs by default */
  promptTimeoutInMs?: number;
  /** Pause all timers and event tracking */
  pause?: boolean;
  /** Function called when idleTimeoutInMs has passed */
  onIdle: () => void;
  /** Function called when an activity event is detected and the timers are reset */
  onActive: () => void;
  /** Function called  30 seconds (or propmptTimeoutInMs) before onActive */
  onPrompt?: () => void;
  /* Configuration for sending messages to other tabs */
  messageConfig?: {
    /** Debounce time in milliseconds before sending messages to other tabs. Default 500ms */
    delay?: number;
    /** Maximum wait time in milliseconds before sending messages to other tabs. Default 1000ms */
    maxWait?: number;
    /** Send first message immediately?. Default true */
    leading?: boolean;
  };
}

interface CommonProps {
  lastActiveTime: number;
}

const THIRTY_SECONDS_IN_MS = 30 * 1000;

export const BROADCAST_CHANNEL_KEY = 'useTrackIdleTimeGlobally';

export const LAST_ACTIVE_TIME_IN_MS_KEY = `${BROADCAST_CHANNEL_KEY}_lastActiveTimeInMs`;

export const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'wheel',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'visibilitychange',
  'focus',
] as const;

const setLastActiveTime = ({ lastActiveTime }: CommonProps) => {
  localStorage.setItem(LAST_ACTIVE_TIME_IN_MS_KEY, String(lastActiveTime));
};

const postLastActiveTime = (config: CommonProps, channel: BroadcastChannel | null) => {
  channel?.postMessage(config).catch(console.error);
};

export function useTrackIdleTimeGlobally({
  onIdle,
  onActive,
  onPrompt,
  pause = false,
  idleTimeoutInMs = null,
  promptTimeoutInMs,
  messageConfig,
}: Config) {
  if (idleTimeoutInMs !== null && idleTimeoutInMs < THIRTY_SECONDS_IN_MS * 2) {
    throw new Error('idleTimeoutInMs cannot be less than 60 seconds');
  }

  if (idleTimeoutInMs && promptTimeoutInMs && promptTimeoutInMs >= idleTimeoutInMs) {
    throw new Error('promptTimeoutInMs must be less than idleTimeoutInMs');
  }

  const isInactive = pause || !idleTimeoutInMs;

  const channel = useRef(
    isInactive ? null : new BroadcastChannel<{ lastActiveTime: number }>(BROADCAST_CHANNEL_KEY)
  );

  const lastActiveTime = useRef<number | null>(
    parseInt(localStorage.getItem(LAST_ACTIVE_TIME_IN_MS_KEY) ?? '') || null
  );
  const currentTimeout = useRef<Timeout | null>(null);
  const promptTimeout = useRef<Timeout | null>(null);

  const { delay, ...postConfig } = { ...defaultMessageConfig, ...messageConfig };
  const debouncedPostLastActiveTime = useDebouncedCallback(
    ({ lastActiveTime }: CommonProps) => {
      postLastActiveTime({ lastActiveTime }, channel?.current);
    },
    delay,
    postConfig
  );

  const setTimer = useCallback(
    (
      {
        readStorage,
        lastActiveTime: forcedLastActiveTime,
      }: { readStorage?: boolean; lastActiveTime?: number | null } = {
        readStorage: false,
        lastActiveTime: null,
      }
    ) => {
      if (isInactive) {
        return;
      }

      const lastRecordedActiveTime = forcedLastActiveTime ?? lastActiveTime.current;

      const shouldUseLastActiveTime =
        (forcedLastActiveTime ?? readStorage) && lastRecordedActiveTime;

      const diff = shouldUseLastActiveTime ? Date.now() - lastRecordedActiveTime : 0;

      const timeout = setTimeout(() => {
        onIdle();
      }, idleTimeoutInMs - diff);
      currentTimeout.current = timeout;
      lastActiveTime.current = Date.now();

      if (onPrompt) {
        const promptTimeoutMs = promptTimeoutInMs
          ? idleTimeoutInMs - promptTimeoutInMs
          : idleTimeoutInMs - THIRTY_SECONDS_IN_MS;

        promptTimeout.current = setTimeout(() => {
          onPrompt();
          if (promptTimeout.current) {
            clearTimeout(promptTimeout.current);
          }
        }, promptTimeoutMs - diff);
      }
    },
    [idleTimeoutInMs, onIdle, onPrompt, isInactive, promptTimeoutInMs]
  );

  const clearTimer = useCallback(() => {
    if (currentTimeout?.current) {
      clearTimeout(currentTimeout.current);
      currentTimeout.current = null;
    }

    if (promptTimeout?.current) {
      clearTimeout(promptTimeout.current);
      promptTimeout.current = null;
    }
  }, []);

  const restartTimer = useCallback(
    (config?: { lastActiveTime: number }) => {
      clearTimer();
      setTimer(config);
    },
    [clearTimer, setTimer]
  );

  const handleBroadcastChannelMessage = useCallback(
    (config: CommonProps) => {
      clearTimer();
      if (isInactive) {
        return;
      }
      onActive();
      restartTimer(config);
      setLastActiveTime(config);
    },
    [clearTimer, isInactive, onActive, restartTimer]
  );

  const onActivity = useCallback(() => {
    clearTimer();
    if (isInactive) {
      return;
    }
    onActive();
    restartTimer();
    const config = { lastActiveTime: Date.now() };
    setLastActiveTime(config);
    debouncedPostLastActiveTime(config);
  }, [clearTimer, debouncedPostLastActiveTime, isInactive, onActive, restartTimer]);

  const bindEvents = useCallback(() => {
    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, onActivity);
    });
  }, [onActivity]);

  const unbindEvents = useCallback(() => {
    ACTIVITY_EVENTS.forEach((event) => {
      document.removeEventListener(event, onActivity);
    });
  }, [onActivity]);

  // Start timers
  useEffect(() => {
    if (isInactive) {
      return;
    }

    setTimer({ readStorage: true });

    return () => {
      clearTimer();
    };
  }, [clearTimer, isInactive, setTimer]);

  // Bind events
  useEffect(() => {
    if (isInactive) {
      return;
    }

    bindEvents();
    return () => {
      unbindEvents();
    };
  }, [bindEvents, isInactive, unbindEvents]);

  // Listen for messages
  useEffect(() => {
    if (isInactive) {
      return;
    }

    if (!channel.current) {
      channel.current = new BroadcastChannel<{ lastActiveTime: number }>(BROADCAST_CHANNEL_KEY);
    }

    channel.current.onmessage = (event) => {
      if (event?.lastActiveTime) {
        handleBroadcastChannelMessage({ lastActiveTime: event.lastActiveTime });
      }
    };

    return () => {
      channel.current
        ?.close()
        .catch(console.error)
        .finally(() => {
          channel.current = null;
        });
    };
  }, [handleBroadcastChannelMessage, isInactive]);
}
